/**
 * 63.1d step 4, the brain's half (lane B; the transport's half is core/ride.ts, lane F's): a squad ordered to **ride** a
 * transport to a place walks to its seats' entries and gets in (the seat intent: E takes the free seat whose entry is
 * nearest); the driver waits for the squad to board, then drives to the place; there, everyone gets out (behind the
 * ramp — the seat's own exit) and carries on with the order's place on foot. Off with the transport (no transport, no
 * ride order). Pure: state and the order in, a step or an intent out.
 *   - `rideStep` (on foot): board the seat `rideSeatFor` names, at its entry — or walk to the place (the ride arrived, or
 *     no seat left for it) — or nothing (the transport is gone: the order lapses in core).
 *   - `rideIntent` (seated in the order's transport): the driver waits up to RIDE_WAIT_SECONDS for squadmates walking to
 *     board it (within RIDE_COMING_PEOPLE), then drives at the place (steer at it, ease off turning hard, slow near it), brakes on arrival and gets out;
 *     a passenger sits, and gets out on arrival or when nobody is driving any more.
 */
import type { GameState, PlayerState, StructureState, UnitIntent, Vec3 } from '../types';
import type { SquadOrder } from '../types/inputs';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { personSurfaceRadius } from '../core/fineTerrain';
import { getSurfaceRadius } from '../core/surface';
import { datan2, dcos, dsin } from '../core/dmath';
import { rideArrived, rideEntry, rideSeatFor } from '../core/ride';
import { orderPlace } from '../core/orderForms';
import { seatUnit } from '../core/seats';
import { bikeHeading, driveSpec } from '../core/drive';
import { footprintObstacles, movingHullNear, movingShapes, personHip, vehicleMover } from '../core/hits';
import { steerAround, type SteerAvoid } from '../core/steer';
import { VEHICLE_MODEL_SIZE } from '../constants';
import { obstaclesNear, projectToTangent, rightOf, stepWalk } from '../core/surfaceWalk';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { squadOf } from '../core/squads';
import { PHYS_DT, ROUTE } from '../constants';
import { routeStep } from '../core/planetRoutes';
import { roadsOf } from '../core/roads';
import { brainFace } from '../core/brainFace';
import { apcInContact, APC_REACH_PEOPLE, BASE_PARTY_PEOPLE, BASE_REACH_PEOPLE } from './apcLoop';
import { moduleRouteStep } from '../core/roomGraph';
import { modularBuildingsOn } from '../core/modules';
import { convoyDrive } from '../core/convoyMarch';

export type RideOrder = Extract<SquadOrder, { kind: 'ride' }>;
/** How long a driver waits for its squad to board before it goes (s), and how far off a squadmate still counts as coming (people). */
export const RIDE_WAIT_SECONDS = 20;
export const RIDE_COMING_PEOPLE = 15;
/** How slow the transport must be for its riders to get out (× walk speed); how slow and for how long it counts as stuck. */
const RIDE_STOPPED = 0.3, RIDE_STUCK_SPEED = 0.2, RIDE_STUCK_SECONDS = 3;

const IDLE: UnitIntent = { forward: 0, right: 0, turn: 0, aimPitch: 0, fire: false, jump: false };

/** On foot under a ride order: what to do. */
export type RideStep = { kind: 'board'; structureId: number; seat: number; entry: Vec3 } | { kind: 'place' } | null;

export function rideStep(state: GameState, unit: PlayerState, order: RideOrder, gaveUpOn?: number, rankBase = 0): RideStep {
  const t = state.structures.find(s => s.id === order.structureId);
  if (!t || t.health <= 0 || t.planetIndex !== unit.planetIndex) return null;
  if (rideArrived(state, order) || gaveUpOn === t.id) return { kind: 'place' };   // arrived, or this ride given up: on foot
  const seat = rideSeatFor(t, unit.id, order.convoy ? 0 : rankBase + boardingRank(state, unit, t.planetIndex));   // (63.1l: a second squad's after the first's)   // (69.4 step 5: a supply run's driver is the task's one unit: the driver's seat)
  if (seat === null) return { kind: 'place' };   // no seat left for it: on foot
  return { kind: 'board', structureId: t.id, seat, entry: rideEntry(state, t, seat) };
}

/** 63.1h: a member's place among its squad's living members on foot on `planetIndex`, in id order (it takes that seat of `boardingOrder`). */
export function boardingRank(state: GameState, unit: PlayerState, planetIndex: number): number {
  const sq = squadOf(state, unit.id);
  if (!sq) return 0;
  let rank = 0;
  for (const id of sq.unitIds) {
    if (id >= unit.id) continue;
    const p = state.players.find(q => q.id === id);
    if (p && p.alive && !p.mount && p.planetIndex === planetIndex) rank++;
  }
  return rank;
}

/** The seat of `structureId` a unit's plan walks it to board (`board:<id>:<seat>`), or null. */
function boardingSeatOf(state: GameState, unitId: number, structureId: number): number | null {
  const key = brainFace(state, unitId)?.plan?.dest?.key;
  const m = key ? /^board:(\d+):(\d+)/.exec(key) : null;
  return m && Number(m[1]) === structureId ? Number(m[2]) : null;
}

/** 63.1l: how long a driver waits at first for any squadmate near (s), walking to board or not. */
const BOARD_WINDOW = 4;
/** Whether a living squadmate of `unit` stands on foot within RIDE_COMING_PEOPLE of `t`. */
function squadNear(state: GameState, unit: PlayerState, t: StructureState): boolean {
  const sq = squadOf(state, unit.id);
  if (!sq) return false;
  const planet = state.planets[t.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(state).k, n = vecNormalize(t.surfaceDir);
  return sq.unitIds.some(id => {
    if (id === unit.id) return false;
    const p = state.players.find(q => q.id === id);
    return !!p && p.alive && !p.mount && p.planetIndex === t.planetIndex && vecLength(vecSub(vecNormalize(p.surfaceDir), n)) * planet.radius <= RIDE_COMING_PEOPLE * person;
  });
}

/** The squadmates still coming to `t`: alive, on foot, on its planet, within RIDE_COMING_PEOPLE of it (ids in order). */
function coming(state: GameState, unit: PlayerState, t: StructureState): number[] {
  const planet = state.planets[t.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const n = vecNormalize(t.surfaceDir);
  // (63.1l: any teammate walking to board it — a second squad fills the bays — not its own squad's only)
  return state.players.filter(q => q.teamIndex === unit.teamIndex).map(q => q.id).filter(id => {
    if (id === unit.id) return false;
    const p = state.players.find(q => q.id === id);
    if (!p || !p.alive || p.mount || p.planetIndex !== t.planetIndex) return false;
    // (only a squadmate walking to board this transport is coming — one near but about something else, a fight, a crate, is
    // not waited for: the driver sat out its whole wait for them and the shells came first, the ride trace of Oct 11)
    const key = brainFace(state, id)?.plan?.dest?.key;
    if (!key || !key.startsWith(`board:${t.id}:`)) return false;
    return vecLength(vecSub(vecNormalize(p.surfaceDir), n)) * planet.radius <= RIDE_COMING_PEOPLE * person;
  });
}

/**
 * The owner on the vehicle reading (Oct 21): "is there some rule about squad membership in a vehicle though? I could see that
 * causing problems". The squadmates a driver waits for out of contact (its whole RIDE_WAIT_SECONDS at most): every living
 * one on foot within the ride's reach of `t` (the loop's: APC_REACH_PEOPLE, BASE_REACH_PEOPLE at the base) — of its party
 * when the ride names one — whatever it is about. Only those already walking to board, within RIDE_COMING_PEOPLE, were
 * waited for, and the loop gives a squad its ride from up to 40 people (120 at the base): a third of the squads' rides on the
 * ¼ board left a member on foot by the vehicle (13 of 41), 12 of 15 of them with a seat free. Never a convoy's (its one driver),
 * a technical's, or with every seat taken.
 */
function squadWaited(state: GameState, unit: PlayerState, t: StructureState, order: RideOrder & { party?: number[] }): number[] {
  const sq = squadOf(state, unit.id);
  // (nor a technical's — 63.1r crews it from a squad and the rest walk, by design: its driver sat out its whole wait for them —
  // nor with no seat free: nobody more can get in)
  if (!sq || order.convoy || t.type === 'technical' || !(t.seats ?? []).some(x => !x.occupant)) return [];
  const planet = state.planets[t.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(state).k, n = vecNormalize(t.surfaceDir);
  const base = state.board?.bases.find(b => b.team === unit.teamIndex)?.dir ?? state.actionHomes?.[unit.teamIndex];
  const reach = (base && vecLength(vecSub(vecNormalize(base), n)) * planet.radius <= BASE_PARTY_PEOPLE * person ? BASE_REACH_PEOPLE : APC_REACH_PEOPLE) * person;
  return (order.party ?? sq.unitIds).filter(id => {
    if (id === unit.id) return false;
    const p = state.players.find(q => q.id === id);
    return !!p && p.alive && !p.mount && !p.boarded && p.planetIndex === t.planetIndex && vecLength(vecSub(vecNormalize(p.surfaceDir), n)) * planet.radius <= reach;
  });
}

/** What the ride keeps between ticks in the brain: when the driver got in, ticks stuck, backing up until, tries made; and
 * `gaveUp` — set when it got out short of the place (stuck for good, or nobody driving), so the caller stops it boarding again. */
export interface RideMemory { since?: number; stuck?: number; backUntil?: number; tries?: number; gaveUp?: boolean; side?: number; /** 69.38: rounds of tries begun again in a marching column (MARCH_TRY_ROUNDS at most) */ rounds?: number; /** 69.88: the back-up's wheel, toward the drivable line, when the ground at its nose stopped it */ backTurn?: number }
/** A stuck transport backs up this long (s), at most this many times, before its riders get out and walk. */
// (backing up 2 s left a 6×6 still at the rock it had met, its turning circle too wide to clear it: 4 s, the nose swung
// toward the side the steer chose round it)
const RIDE_BACK_SECONDS = 4, RIDE_TRIES = 3;
/** 69.38: in a marching column, the rounds of RIDE_TRIES begun again round the other side before the give-up after all (a hull wedged for good sat 380 s, its riders aboard). */
const MARCH_TRY_ROUNDS = 3;
/** fix/bot-vehicles-2: a back-up still not moving after this long (s) ends, its try spent. */
const RIDE_BACK_BLOCKED = 1;
/** 63.1l: how long a passenger waits with the driver's seat empty before it gives the ride up (s). */
const RIDE_PASSENGER_WAIT = 5;

/**
 * 63.1i: told to dismount — the ride's arrival on its own: out once `t` has stopped, its driver braking till then (the
 * others sit); out at once from what does not drive (a gun pit, an HMG) or is gone.
 */
export function dismountIntent(state: GameState, unit: PlayerState, t: StructureState | undefined): UnitIntent {
  const leave: UnitIntent = { ...IDLE, seat: 'leave' };
  if (!t?.drive) return leave;
  if (Math.abs(t.drive.speed ?? 0) <= RIDE_STOPPED * personSetOf(state).walkSpeed) return leave;
  return (t.seats ?? []).find(s => seatUnit(s) === unit.id)?.role === 'driver' ? { ...IDLE, forward: -1 } : IDLE;
}

/** 63.1l: the steepest grade a transport is steered up (rise over run): where its engine stalls (accel over the hill's
 * pull the engine does not carry, 0.8 / (6 × 0.15) ≈ 0.89), with a margin. */
const DRIVABLE_GRADE = 0.7;
/** The headings tried either side of a line too steep to drive (radians off it). */
const LINE_SWINGS = [0.4, 0.8, 1.2];

/** The grade (rise over run) from `n` toward `d` (a tangent at `n`) at `ahead` world units, on the ground the drive reads. */
function gradeAlong(state: GameState, t: StructureState, n: Vec3, d: Vec3, ahead: number): number {
  const planet = state.planets[t.planetIndex], ps = personSetOf(state);
  const ground = (dir: Vec3) => (ps.fineAmplitude > 0 ? personSurfaceRadius(planet, dir, ps) : getSurfaceRadius(planet, dir));
  const a = ahead / planet.radius;
  const there = vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(d, dsin(a))));
  return (ground(there) - ground(n)) / ahead;
}

/** `want` if the ground along it (one and two hulls ahead) is drivable, else the nearest heading either side that is. */
function drivableLine(state: GameState, t: StructureState, n: Vec3, want: Vec3, side: number): Vec3 {
  const hull = driveSpec(t.type).wheelbase * PERSON_BODY_HEIGHT * personSetOf(state).k;
  const ok = (d: Vec3) => gradeAlong(state, t, n, d, hull) <= DRIVABLE_GRADE && gradeAlong(state, t, n, d, 2 * hull) <= DRIVABLE_GRADE;
  if (ok(want)) return want;
  const r = rightOf(want, n);
  for (const a of LINE_SWINGS) for (const s of [side >= 0 ? 1 : -1, side >= 0 ? -1 : 1]) {
    const d = vecNormalize(vecAdd(vecScale(want, dcos(a)), vecScale(r, s * dsin(a))));
    if (ok(d)) return d;
  }
  return want;
}

/**
 * 69.86 (story-2's escort 6×6, wedged at home): the side a hull passes the shape nearest its nose on — the side the shape's
 * bearing already gives it (a shape right of its heading passed on its left: `steerAround`'s +1), or null with none near
 * its way. A hull turns only as it rolls, at most its speed over its turning radius, so a near shape passed on its far side
 * had the nose swept across it: the 6×6, boxed in at its pad, kept the side an earlier shape gave it, swung toward the parked
 * tracked carrier ahead-right of it for the follow point past it, met its hull 3.5 people off and backed and tried again for
 * two minutes, its driver out at the last. Near: ahead, within two turning radii and the shape's reach, in the way of its
 * nose's line or of the line it wants (`to`, a unit tangent).
 */
export function noseSide(n: Vec3, heading: Vec3, to: Vec3, avoid: readonly SteerAvoid[], planetRadius: number, bodyRadius: number, turnRadius: number): number | null {
  const right = rightOf(heading, n);
  let best: { dist: number; side: number } | null = null;
  for (const a of avoid) {
    if (a.kind !== 'disc') continue;
    const d = vecNormalize(a.dir), o = vecScale(vecSub(d, vecScale(n, vecDot(d, n))), planetRadius);   // (its offset in the tangent plane, world units)
    const ahead = vecDot(o, heading), dist = vecLength(o), reach = a.radius + bodyRadius;
    if (ahead <= 0 || dist > 2 * turnRadius + reach) continue;
    const inWay = (dir: Vec3): boolean => { const along = vecDot(o, dir); return along > 0 && vecLength(vecSub(o, vecScale(dir, along))) < reach; };
    if (!inWay(heading) && !inWay(to)) continue;
    if (!best || dist < best.dist) best = { dist, side: vecDot(o, right) >= 0 ? 1 : -1 };
  }
  return best?.side ?? null;
}

/** 68.25: whether a ride order keeps a seat of this role aboard when it arrives. */
export function keptSeat(order: RideOrder, role: string | undefined): boolean {
  return (order.keep === 'gunner' || order.keep === 'crew') && role === 'weapon' || order.keep === 'crew' && role === 'driver';
}

/** Seated in the order's transport `t` (the caller checked): the rider's intent this tick. */
export function rideIntent(state: GameState, unit: PlayerState, t: StructureState, order: RideOrder, tick: number, mem: RideMemory): UnitIntent {
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const seats = t.seats ?? [];
  const mine = seats.findIndex(s => seatUnit(s) === unit.id);
  const speed = Math.abs(t.drive?.speed ?? 0);
  const leave: UnitIntent = { ...IDLE, seat: 'leave' };
  const stopped = speed <= RIDE_STOPPED * ps.walkSpeed;
  // 69.38 (§5.28.1, DECISIONS 274): a mounted play's convoy on the march — the driver keeps its place in the column; the
  // order's arrival is not read while it marches (a tank's or technical's order is its follow point: it braked whenever
  // within RIDE_ARRIVE of it, 8 people of a 15-people spacing, and fell back; the march's hold is that rule)
  const march = state.config.mountedAssault && seats[mine]?.role === 'driver' ? convoyDrive(state, t.id) : null;
  if (!march && rideArrived(state, order)) {
    // (68.25: the seats the order keeps stay — the driver holds the hull there; a gunner's gun is the brain's, the caller's)
    if (keptSeat(order, seats[mine]?.role)) return seats[mine]?.role === 'driver' && !stopped ? { ...IDLE, forward: -1 } : IDLE;
    return stopped ? leave : seats[mine]?.role === 'driver' ? { ...IDLE, forward: -1 } : IDLE;
  }
  if (seats[mine]?.role !== 'driver') {
    // a passenger: sits — unless nobody drives any more (the driver got out or fell), then out
    const driver = seats.find(s => s.role === 'driver');
    // (63.1l: not while a squadmate walks to the driver's seat — the seats go by rank, not by who is nearest, so a bay is
    // often taken first: it got out, gave the ride up, and the squad's ride was lost)
    const driverSeat = seats.findIndex(s => s.role === 'driver');
    // (nor in its first RIDE_PASSENGER_WAIT seconds with the driver's seat empty: at a ride's first tick nobody walks to it yet)
    if (driver && seatUnit(driver) === null) mem.since ??= tick; else delete mem.since;
    if (driver && seatUnit(driver) === null && stopped && (tick - (mem.since ?? tick)) * PHYS_DT >= RIDE_PASSENGER_WAIT && !coming(state, unit, t).some(id => boardingSeatOf(state, id, t.id) === driverSeat)) { mem.gaveUp = true; return leave; }
    return IDLE;
  }
  mem.since ??= tick;
  // wait for the squad walking to it (a while at most) — out of contact, for every squadmate on foot in reach (squadWaited)
  const waiting = (tick - mem.since) * PHYS_DT < RIDE_WAIT_SECONDS;
  if (waiting && (coming(state, unit, t).length > 0 || (squadWaited(state, unit, t, order).length > 0 && !apcInContact(state, unit.teamIndex, t, tick)))) return stopped ? IDLE : { ...IDLE, forward: -1 };
  // 63.1l (the owner: "taking more guys before leaving"): its first BOARD_WINDOW seconds, a squadmate on foot within
  // RIDE_COMING_PEOPLE is waited for whatever it is about (it left with one of six seats taken, the squad 2–10 people off)
  if ((tick - mem.since) * PHYS_DT < BOARD_WINDOW && squadNear(state, unit, t)) return stopped ? IDLE : { ...IDLE, forward: -1 };
  // (the march: the driver holds, or steers for its point at the column's pace, till the halt — not for the order's place)
  if (march?.hold) return stopped ? IDLE : { ...IDLE, forward: -1 };
  const place = march ? { planetIndex: t.planetIndex, surfaceDir: march.goal } : orderPlace(state, order);
  if (!place || place.planetIndex !== t.planetIndex) return stopped ? leave : { ...IDLE, forward: -1 };
  const planet = state.planets[t.planetIndex];
  const n = vecNormalize(t.surfaceDir), heading = bikeHeading(t);
  const far = vecLength(vecSub(vecNormalize(place.surfaceDir), n)) * planet.radius;
  // §0.11 step C (GameConfig.planetRoutes): a drive longer than ROUTE.minPeople follows the planet's route graph on wheels
  const aim = state.config.planetRoutes && far > ROUTE.minPeople * person ? routeStep(planet, n, place.surfaceDir, 'wheels', roadsOf(state, t.planetIndex)) : place.surfaceDir;   // (216 (4): on the roads, with them on)
  // 69.14 (the owner: "bots can get stuck in there"): out of a walled compound — the motor pool's, its unpacked vehicle driving
  // out — or into one, by its gate: the route graph's next door, as a walker takes it (core/roomGraph.ts moduleRouteStep);
  // straight at the place it drove into the pool's front wall, five people ahead of its pad and twelve from the gate
  const gated = state.config.motorPool || modularBuildingsOn(state.config) ? moduleRouteStep(state, t.planetIndex, n, aim) : null;
  const goTo = gated ?? aim;
  const to = projectToTangent(vecSub(vecNormalize(goTo), n), n);
  // round what lies across its way (the walk's look-ahead steer, core/steer.ts, sized for the hull: the shapes it drives
  // against, its own radius, a few hulls ahead), keeping the side it chose, and a place beside a shape driven to rather
  // than round (the local steer's goal rule, 62.43). Not its slide at contact: a hull cannot turn 90° standing still, so
  // sliding along what it touches only held it there (the ride took 89 s against 45 s); backing up does that job
  // (below)
  const spec = driveSpec(t.type), radius = spec.radius * person;
  const size = VEHICLE_MODEL_SIZE[t.type as keyof typeof VEHICLE_MODEL_SIZE] ?? VEHICLE_MODEL_SIZE.motorcycle;
  const fp = footprintObstacles(state, t.planetIndex, vehicleMover(radius, size.height * person));
  const look = 6 * radius;
  const avoid: SteerAvoid[] = obstaclesNear(fp, n, (look + fp.maxRadius + 2 * radius) / planet.radius).map(o => ({ kind: 'disc', dir: o.dir, radius: o.radius }));
  // 69.37 (the column's bug 2; behind GameConfig.mountedAssault): and the other hulls — a vehicle is no walk obstacle (it
  // moves), so the steer never saw one while the hull test stopped it there (core/drive.ts movingHullNear): story-1's escort
  // 6×6s drove into their sides' mortar carriers parked 3–7 people off at home, backed up, drove in again, and gave up
  if (state.config.mountedAssault) {
    // (69.38: each widened by how far its own hull reaches past its body's radius — the steer passes a shape at the body's
    // radius, its width; hull against hull, its length: passing at 3.1 people from its mortar carrier the 6×6 met it, 3.8
    // apart, backed off and drove in again for 160 s)
    const shapes = movingShapes(state, t.planetIndex), past = Math.max(0, (shapes.find(sh => sh.ref.id === t.id)?.reach ?? radius) - radius);
    for (const sh of shapes) {
      // (where it stands: its structure's ground point — the shape's `dir` is its hull's up, tilted with the ground)
      const at = sh.ref.id !== t.id ? state.structures.find(x => x.id === sh.ref.id)?.surfaceDir : undefined;
      if (at && vecLength(vecSub(vecNormalize(at), n)) * planet.radius <= look + sh.reach + past + 2 * radius) avoid.push({ kind: 'disc', dir: at, radius: sh.reach + past });
    }
  }
  // (69.86: in a mounted play's world, a shape near its nose passed on the side it already lies — a tracked body pivots, so not it)
  const pass = state.config.mountedAssault && !spec.pivotRate ? noseSide(n, heading, vecNormalize(to), avoid, planet.radius, radius, (spec.turnRadius.min + spec.turnRadius.perSpeed * speed / person) * person) : null;
  const steered = steerAround(n, vecNormalize(to), avoid, { planetRadius: planet.radius, bodyRadius: radius, lookAhead: look, margin: 0.5 * radius, side: pass ?? mem.side ?? null, goal: goTo });
  if (steered.side === null) delete mem.side; else mem.side = steered.side;
  // 63.1l (the owner: "better at avoiding getting stuck"): a line it can drive — half its stuck time was on a grade too
  // steep to climb, nothing in its way (Foothold, four runs): along the steered line, the grade one and two hulls ahead;
  // too steep, the nearest heading either side (the steer's side first) that is not, else the line as it was
  const dir = drivableLine(state, t, n, vecNormalize(steered.move), mem.side ?? 1), right = rightOf(heading, n);
  const err = datan2(vecDot(dir, right), vecDot(dir, heading));
  const steer = Math.max(-1, Math.min(1, err * 2));
  // stuck (throttle open, barely moving — facing something it cannot turn away from standing still): back up a moment,
  // the wheel the other way, and try again; after RIDE_TRIES, everyone out — on foot from here
  const swing = -(mem.side ?? (steer >= 0 ? 1 : -1));
  // 69.37 (the column's bug 2): in a marching column the give-up does not empty the bay — the tries start again round the
  // other side (story-1's escort APC gave up 2,954 people short and its riders walked; the column waits for it, the head's
  // hold, and the play's march has its own limit, MOUNTED.marchSeconds)
  const keepTrying = (): boolean => {
    if (!march || (mem.rounds ?? 0) >= MARCH_TRY_ROUNDS) return false;   // (69.38: MARCH_TRY_ROUNDS rounds, then out after all)
    mem.rounds = (mem.rounds ?? 0) + 1; mem.tries = 0; mem.side = -(mem.side ?? (steer >= 0 ? 1 : -1));
    return true;
  };
  // 69.38 (behind GameConfig.mountedAssault): a hull standing close behind ends a back-up, or keeps it from starting — backing
  // up looks at nothing behind it: story-4's 6×6, stalled at its pad, backed into its side's parked tank and was wedged between
  // the two (its hull 1.7 people from the tank's)
  const hullBehind = (): boolean => {
    if (!state.config.mountedAssault) return false;
    const shapes = movingShapes(state, t.planetIndex), own = shapes.find(sh => sh.ref.id === t.id)?.reach ?? radius;
    const near = shapes.some(sh => {
      const at = sh.ref.id !== t.id ? state.structures.find(x => x.id === sh.ref.id)?.surfaceDir : undefined;
      if (!at) return false;
      const off = vecSub(vecNormalize(at), n), gap = vecLength(off) * planet.radius;   // (the chord: the tangent's direction alone is a unit)
      return gap <= own + sh.reach + 0.5 * person && vecDot(projectToTangent(off, n), heading) < 0;
    });
    if (!near) return false;
    // 69.88: and in the way of the back-up — the drive's own hull test (core/drive.ts moveBike: at its rear, half a wheelbase
    // back) along its first two people. A hull beside is not behind: story-3's escort 6×6, a parked technical 3.6 people at
    // its nose and a supply truck 2.6 off its side (a touch back of its middle), counted the truck behind, never backed up,
    // and spent fifteen tries in place, its riders out at 69 s; straight back was clear
    const rear = 0.5 * spec.wheelbase * person;
    return [0.5, 1, 1.5, 2].some(k => {
      const a = -(k * person + rear) / planet.radius, p = vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(heading, dsin(a))));
      return movingHullNear(personHip(planet, p, ps), radius, t.planetIndex, state, t.id);
    });
  };   // backing up, the wheel turned the other way swings the nose toward the steer's side
  // (69.88: the ground half a person ahead of its nose too steep for it, the walk step alone — no shapes)
  const steepAhead = (): boolean => stepWalk(t.surfaceDir, heading, planet, [], 1, 0, 0.5 * person, 1,
    { unitRadius: radius, maxSlope: ps.maxSlope * spec.slopeReach, fineAmplitude: ps.fineAmplitude, fineWavelength: ps.fineWavelength, k: ps.k }).blocked;
  // (fix/bot-vehicles-2: a back-up that has not moved after RIDE_BACK_BLOCKED seconds — blocked behind too — ends there, the
  // try spent: at a few let-outs a hull sat out every back-up's full time going nowhere, a minute before its riders walked)
  if (mem.backUntil !== undefined && tick < mem.backUntil) {
    const backed = Math.round(RIDE_BACK_SECONDS / PHYS_DT) - (mem.backUntil - tick);
    if (!(backed * PHYS_DT >= RIDE_BACK_BLOCKED && speed < RIDE_STUCK_SPEED * ps.walkSpeed) && !hullBehind()) return { ...IDLE, forward: -1, right: mem.backTurn ?? swing };
    mem.backUntil = undefined; delete mem.backTurn;
    if ((mem.tries ?? 0) >= RIDE_TRIES && !keepTrying()) { mem.gaveUp = true; return leave; }
  }
  if (speed < RIDE_STUCK_SPEED * ps.walkSpeed && (t.drive?.throttle ?? 0) > 0) mem.stuck = (mem.stuck ?? 0) + 1; else mem.stuck = 0;
  if ((mem.stuck ?? 0) > Math.round(RIDE_STUCK_SECONDS / PHYS_DT)) {
    mem.stuck = 0; mem.tries = (mem.tries ?? 0) + 1;
    if (mem.tries > RIDE_TRIES && !keepTrying()) { mem.gaveUp = true; return leave; }
    if (hullBehind()) { mem.side = -(mem.side ?? (steer >= 0 ? 1 : -1)); return { ...IDLE, forward: 1, right: -swing }; }   // (no room behind: the try spent forward, round the other side)
    mem.backUntil = tick + Math.round(RIDE_BACK_SECONDS / PHYS_DT);
    // 69.88 (behind GameConfig.mountedAssault): stopped by the ground at its nose — too steep, no shape in it — the back-up's
    // wheel turns the nose toward the drivable line. The drive yaws the way the wheel points backing up too, so the wheel the
    // other way undid the try's turn and the next try met the same slope: story-8's escort 6×6, its follow point over a hill
    // 63° off its nose, backed to 150° off and turned back into the hillside nine times, its riders out at 200 s; the ground
    // round the hill's flank (90°–120° off, which drivableLine chose) lay a turn further on
    if (state.config.mountedAssault && steepAhead()) mem.backTurn = steer >= 0 ? 1 : -1; else delete mem.backTurn;
    return { ...IDLE, forward: -1, right: mem.backTurn ?? swing };
  }
  // (69.38: in a column no faster than its pace — and the throttle open otherwise: from a stand on a grade the eased throttle
  // (0.3 turning hard, 0.5 near the place) did not carry the hill's pull, so the hull stalled, counted itself stuck and backed
  // up; and a follower's point is always near. Turning hard it eases off only once rolling)
  // (and braked past it by a tenth: shut, it coasted on downhill — the column's head above its pace a quarter of the march,
  // its followers held to the pace falling back)
  const throttle = march
    ? (speed > 1.1 * march.pace ? -1 : speed >= march.pace ? 0 : Math.abs(err) > 1.2 && speed > RIDE_STUCK_SPEED * ps.walkSpeed ? 0.3 : 1)
    : Math.abs(err) > 1.2 ? 0.3 : far < 3 * 8 * person ? 0.5 : 1;
  return { ...IDLE, forward: throttle, right: steer };
}
